import { LoadingSpinner } from "components/LoadingSpinner";
import {
  ICelebrationOrigin,
  WorklistCelebration,
} from "pages/EligibilityPage/WorklistCelebration";
import { Button } from "primereact/button";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  useGetModuleQuizQuery,
  useGetMyTrainingQuery,
  useGetQuizQuery,
  useSubmitModuleQuizMutation,
  useSubmitQuizMutation,
} from "store/queries/training";
import { userSelector } from "store/slices/user";
import styled from "styled-components";
import { MODULES } from "training/curriculum/modules";
import { notePassedModule } from "training/engine/session";
import { trackTraining } from "training/engine/telemetry";
import {
  CERTIFICATE_PATH,
  firstUnpassedModule,
  keepRightAnswers,
  moduleQuizPath,
  passedModules,
  QUIZ_PATH,
  toAnswers,
  whereItLives,
} from "training/quizHelpers";
import { TourModule } from "training/types";
import { IQuizGrade, IQuizQuestionResult } from "types/Training";

const Page = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 24px 0 48px;
`;

/** Same voice as the tour's popover header: purple eyebrow, title, one-sentence purpose. */
const Header = styled.header`
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--brand-gray-200);
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--brand-purple-700);
  font-size: 0.8rem;
  font-weight: var(--font-weight-semibold);
`;

const Title = styled.h1`
  margin: 4px 0 6px;
  font-size: 1.5rem;
  font-weight: var(--font-weight-semibold);
`;

const Purpose = styled.p`
  margin: 0;
  color: var(--brand-gray-700);
`;

const Question = styled.fieldset`
  margin: 0 0 16px;
  padding: 16px;
  border: 1px solid var(--brand-gray-200);
  border-radius: 12px;

  &[data-result="wrong"] {
    border-color: var(--red-300, #fca5a5);
    background: var(--red-50, #fef2f2);
  }

  &[data-result="right"] {
    border-color: var(--green-300, #86efac);
  }
`;

const Legend = styled.legend`
  float: left;
  width: 100%;
  margin-bottom: 10px;
  padding: 0;
  font-weight: var(--font-weight-semibold);
`;

const Choice = styled.label`
  clear: left;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 6px 4px;
  border-radius: 6px;
  cursor: pointer;

  input {
    margin-top: 3px;
    accent-color: var(--brand-purple-700);
  }

  &[data-disabled="true"] {
    cursor: default;
  }
`;

const Feedback = styled.div`
  margin-top: 10px;
  font-size: 0.875rem;

  p {
    margin: 0 0 4px;
  }
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
`;

const Score = styled.p`
  margin: 0;
  font-weight: var(--font-weight-semibold);
`;

const Success = styled.section`
  margin: 0 0 20px;
  padding: 20px;
  border: 2px solid var(--brand-purple-700);
  border-radius: 12px;
  background: var(--brand-blue-25);
  text-align: center;

  .pi-verified {
    color: var(--brand-purple-700);
    font-size: 2.5rem;
  }

  h2 {
    margin: 8px 0 4px;
    color: var(--brand-purple-700);
  }

  p {
    margin: 0 0 12px;
  }
`;

const ErrorText = styled.p`
  margin: 0;
  color: var(--red-600, #dc2626);
`;

const ModuleName = styled.p`
  margin: 0 0 6px;
  color: var(--brand-gray-700);
  font-weight: var(--font-weight-semibold);
`;

/** The modules in the tour (TourModule 1–6). */
const MODULES_COUNT = 6;

const COUNT_WORDS: Record<number, string> = {
  3: "three",
  5: "five",
  12: "twelve",
};

const countWord = (n: number) => COUNT_WORDS[n] ?? String(n);

const capitalize = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1);

function ResultFeedback({
  result,
  choices,
}: {
  result: IQuizQuestionResult;
  choices: string[];
}) {
  if (result.correct) return null;
  const where = whereItLives(result.stepId);
  return (
    <Feedback data-testid={`quiz-feedback-${result.questionId}`}>
      <p>
        <strong>Right answer:</strong> {choices[result.correctIndex]}
      </p>
      {where ? (
        <p>
          <strong>Where it lives:</strong> Module {where.module},{" "}
          {where.moduleTitle} — {where.what}
        </p>
      ) : null}
    </Feedback>
  );
}

interface IQuizPageProps {
  /** A module's quiz (1–6); without it, the knowledge check. */
  module?: TourModule;
}

/**
 * CLE-3015 — the quizzes, one page for both kinds (John's review):
 *
 * - `/training/module/:n/quiz` — a module's quiz. The tour sends the trainee here after the module's last step.
 *   Three questions; pass = 3 of 3 and unlocks the next module: confetti, "Module N complete", and "Start
 *   Module N+1", which reopens the tour on the hospital view (`?training=1`) at that module's title screen.
 *   After Module 6 the button opens the knowledge check instead. Never certifies.
 * - `/training/quiz` — the knowledge check: five questions, pass = 5 of 5, which certifies the user on the
 *   server (confetti, then the certificate), exactly as the twelve-question check did.
 *
 * All questions on one page; Submit waits until every question has an answer. The API grades (it is the only
 * place the answers live) and returns, for each wrong one, the right choice and the curriculum step it "lives"
 * at, which this page names. Try again keeps the right answers and clears the wrong ones; retries are
 * unlimited.
 *
 * Locked: while an earlier module's quiz (or, for the knowledge check, any module's) is unpassed, the page says
 * which one and links to it, without asking the API (which would answer 409). A module quiz already passed shows
 * its "complete" block and the way on. HospitalStaff only (the route's WithRoleProtection sends a SuperAdmin
 * away); a certified user is sent to the hospital view — but not the one who passed a moment ago on this page.
 */
export function QuizPage({ module }: IQuizPageProps) {
  const navigate = useNavigate();
  const { currentAccount } = useSelector(userSelector);
  const { data: trainingData, isLoading: trainingLoading } =
    useGetMyTrainingQuery(currentAccount);

  const [selected, setSelected] = useState<Record<string, number>>({});
  const [grade, setGrade] = useState<IQuizGrade | null>(null);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [celebrate, setCelebrate] = useState<ICelebrationOrigin | null>(null);
  const passedHere = useRef(false);
  const submitRef = useRef<HTMLDivElement>(null);
  const startedTracked = useRef(false);

  const record = trainingData?.data;
  const passed = passedModules(record);
  const lockedBy = record
    ? firstUnpassedModule(passed, module ?? MODULES_COUNT + 1)
    : null;
  const alreadyPassed = !!module && passed.has(module);
  const skipQuestions =
    !record ||
    !!record.clearCertifiedAt ||
    lockedBy !== null ||
    (alreadyPassed && !passedHere.current);

  const finalQuiz = useGetQuizQuery(undefined, {
    skip: !!module || skipQuestions,
  });
  const moduleQuiz = useGetModuleQuizQuery(module ?? 1, {
    skip: !module || skipQuestions,
  });
  const {
    data: quizData,
    isLoading: quizLoading,
    isError: quizError,
  } = module ? moduleQuiz : finalQuiz;
  const [submitQuiz, { isLoading: submittingFinal }] = useSubmitQuizMutation();
  const [submitModuleQuiz, { isLoading: submittingModule }] =
    useSubmitModuleQuizMutation();
  const submitting = submittingFinal || submittingModule;

  const view = quizData?.data;
  const questions = useMemo(() => view?.questions ?? [], [view]);
  const resultsById = useMemo(
    () =>
      new Map((grade?.results ?? []).map((r) => [r.questionId, r] as const)),
    [grade]
  );
  const telemetryBase = {
    accountId: currentAccount ?? "",
    curriculumVersion: record?.curriculumVersion ?? 1,
  };

  useEffect(() => {
    if (module || startedTracked.current || !questions.length || !record) {
      return;
    }
    if (record.clearCertifiedAt) return;
    startedTracked.current = true;
    trackTraining("training_quiz_started", {
      ...telemetryBase,
      attempts: view?.quiz?.attempts ?? 0,
    });
  }, [questions.length, record]);

  if (trainingLoading || quizLoading) return <LoadingSpinner />;

  const hospitalView = currentAccount
    ? `/hospitals/view/${currentAccount}/orders`
    : "/";
  if (record?.clearCertifiedAt && !passedHere.current) {
    return <Navigate to={hospitalView} replace />;
  }

  const total = view?.total ?? questions.length;
  const allAnswered =
    questions.length > 0 &&
    questions.every((q) => selected[q.id] !== undefined);
  const graded = !!grade;
  const passedNow = !!grade?.passed;

  /** After a module quiz: the next module's title screen, or after Module 6 the knowledge check. */
  const nextModule =
    module && module < MODULES_COUNT ? ((module + 1) as TourModule) : null;
  const goOn = () =>
    nextModule ? navigate(`${hospitalView}?training=1`) : navigate(QUIZ_PATH);
  const goOnLabel = nextModule
    ? `Start Module ${nextModule}`
    : "Take the knowledge check";

  const celebrateFrom = () => {
    const rect = submitRef.current?.getBoundingClientRect();
    setCelebrate(
      rect
        ? { x: rect.left + rect.width / 2, y: rect.top }
        : { x: window.innerWidth / 2, y: window.innerHeight / 2 }
    );
  };

  const handleSubmit = async () => {
    setSubmitFailed(false);
    try {
      const answers = toAnswers(selected);
      const response = module
        ? await submitModuleQuiz({ module, answers }).unwrap()
        : await submitQuiz(answers).unwrap();
      const result = response.data;
      if (result.passed) passedHere.current = true;
      setGrade(result);
      if (module) {
        trackTraining("training_module_quiz_submitted", {
          ...telemetryBase,
          module,
          score: result.score,
          attempts: result.attempts,
        });
        if (result.passed) {
          notePassedModule(module);
          celebrateFrom();
        }
      } else {
        trackTraining("training_quiz_submitted", {
          ...telemetryBase,
          score: result.score,
          attempts: result.attempts,
        });
        if (result.passed) {
          trackTraining("training_certified", {
            ...telemetryBase,
            score: result.score,
            attempts: result.attempts,
          });
          celebrateFrom();
        }
      }
      window.scrollTo?.({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("[Clear Certified] submitting the quiz failed", error);
      setSubmitFailed(true);
    }
  };

  const handleTryAgain = () => {
    const wrong = (grade?.results ?? [])
      .filter((r) => !r.correct)
      .map((r) => r.questionId);
    setSelected((current) => keepRightAnswers(current, wrong));
    setGrade(null);
  };

  const header = module ? (
    <Header>
      <Eyebrow>
        <i className="pi pi-verified" aria-hidden />
        Get Clear Certified · Module {module} quiz
      </Eyebrow>
      <Title>Module {module} quiz</Title>
      <ModuleName>{MODULES[module].title}</ModuleName>
      <Purpose>
        Three quick questions on what you just saw. Get one wrong and we show
        you the answer and where it lives. Answer all three right to unlock{" "}
        {nextModule ? `Module ${nextModule}` : "the knowledge check"}; you can
        try as many times as you like.
      </Purpose>
    </Header>
  ) : (
    <Header>
      <Eyebrow>
        <i className="pi pi-verified" aria-hidden />
        Get Clear Certified · Knowledge check
      </Eyebrow>
      <Title>The knowledge check</Title>
      <Purpose>
        Five quick questions from across the six modules. Get one wrong and we
        show you the answer and where it lives. Answer all five right to become
        Clear Certified; you can try as many times as you like.
      </Purpose>
    </Header>
  );

  if (lockedBy !== null) {
    return (
      <Page>
        {header}
        <Success data-testid="quiz-locked">
          <i className="pi pi-lock" aria-hidden />
          <h2>Finish Module {lockedBy} first</h2>
          <p>
            {module
              ? `Module ${module}'s quiz opens once you pass the Module ${lockedBy} quiz.`
              : `The knowledge check opens once you pass all six module quizzes. Next up: Module ${lockedBy}.`}
          </p>
          <Button
            type="button"
            label={`Go to the Module ${lockedBy} quiz`}
            onClick={() => navigate(moduleQuizPath(lockedBy))}
          />
        </Success>
      </Page>
    );
  }

  const moduleComplete = !!module && (passedNow || alreadyPassed);

  return (
    <Page>
      {celebrate ? (
        <WorklistCelebration
          origin={celebrate}
          onDone={() => setCelebrate(null)}
        />
      ) : null}
      {header}

      {moduleComplete && module ? (
        <Success data-testid="quiz-success">
          <i className="pi pi-verified" aria-hidden />
          <h2>Module {module} complete</h2>
          <p>
            {passedNow
              ? `All ${countWord(total)} right. `
              : "You have passed this module's quiz. "}
            {nextModule
              ? `Module ${nextModule}, ${MODULES[nextModule].title}, is unlocked.`
              : "All six modules are done, and the knowledge check is unlocked."}
          </p>
          <Button type="button" label={goOnLabel} onClick={goOn} />
        </Success>
      ) : null}

      {!module && passedNow ? (
        <Success data-testid="quiz-success">
          <i className="pi pi-verified" aria-hidden />
          <h2>You are Clear Certified</h2>
          <p>
            {capitalize(`all ${countWord(total)} right.`)} Your certificate is
            ready to view, print or download.
          </p>
          <Button
            type="button"
            label="View your certificate"
            onClick={() => navigate(CERTIFICATE_PATH)}
          />
        </Success>
      ) : null}

      {graded && !passedNow ? (
        <Score role="status" style={{ marginBottom: 16 }}>
          You got {grade?.score} of {grade?.total}. The ones to fix are marked
          below.
        </Score>
      ) : null}

      {quizError ? (
        <ErrorText role="alert">
          The quiz could not be loaded. Reload the page to try again.
        </ErrorText>
      ) : null}

      {moduleComplete && !graded
        ? null
        : questions.map((question, index) => {
            const result = resultsById.get(question.id);
            let outcome: "right" | "wrong" | undefined;
            if (result) outcome = result.correct ? "right" : "wrong";
            return (
              <Question
                key={question.id}
                data-testid={`quiz-question-${question.id}`}
                data-result={outcome}
              >
                <Legend>
                  {index + 1}. {question.question}
                </Legend>
                {question.choices.map((choice, choiceIndex) => (
                  <Choice
                    // The API's order is stable; the index is the answer.
                    // eslint-disable-next-line react/no-array-index-key
                    key={choiceIndex}
                    data-disabled={graded}
                  >
                    <input
                      type="radio"
                      name={question.id}
                      value={choiceIndex}
                      checked={selected[question.id] === choiceIndex}
                      disabled={graded}
                      onChange={() =>
                        setSelected((current) => ({
                          ...current,
                          [question.id]: choiceIndex,
                        }))
                      }
                    />
                    <span>{choice}</span>
                  </Choice>
                ))}
                {result ? (
                  <ResultFeedback result={result} choices={question.choices} />
                ) : null}
              </Question>
            );
          })}

      {questions.length && !(moduleComplete && !graded) ? (
        <Footer ref={submitRef}>
          {graded && !passedNow ? (
            <>
              <Score>
                You got {grade?.score} of {grade?.total}.
              </Score>
              <Button
                type="button"
                label="Try again"
                icon="pi pi-refresh"
                onClick={handleTryAgain}
              />
            </>
          ) : null}
          {!graded ? (
            <Button
              type="button"
              label="Submit"
              disabled={!allAnswered || submitting}
              loading={submitting}
              onClick={handleSubmit}
            />
          ) : null}
          {!graded && !allAnswered ? (
            <span>Answer every question to submit.</span>
          ) : null}
          {submitFailed ? (
            <ErrorText role="alert">
              Your answers could not be sent. Check your connection and press
              Submit again.
            </ErrorText>
          ) : null}
        </Footer>
      ) : null}
    </Page>
  );
}

/** `/training/module/:module/quiz`: module 1–6, anything else back to the hospital view. */
export function ModuleQuizPage() {
  const { module: param } = useParams();
  const { currentAccount } = useSelector(userSelector);
  const module = /^[1-6]$/.test(param ?? "")
    ? (Number(param) as TourModule)
    : null;
  if (!module) {
    return (
      <Navigate
        to={currentAccount ? `/hospitals/view/${currentAccount}/orders` : "/"}
        replace
      />
    );
  }
  // A new module remounts the page, so nothing from the previous module's attempt carries over.
  return <QuizPage key={module} module={module} />;
}
